<template>
  <div>
    <b-card>
      <b-row class="mb-2">
        <b-col md="6">
          <b-form-group label="Seleccione un Docente">
            <v-select
              v-model="selectedTeacher"
              :options="teachers"
              label="nombre"
              :loading="loadingTeachers"
              @input="loadCompetencies"
            />
          </b-form-group>
        </b-col>
        <b-col md="6" class="d-flex align-items-start justify-content-end">
          <b-button 
            variant="primary" 
            @click="saveCompetencies"
            :disabled="!selectedTeacher || saving"
            class="mt-1"
          >
            <feather-icon icon="SaveIcon" class="mr-1" />
            {{ saving ? 'Guardando...' : 'Guardar Competencias' }}
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-row v-if="selectedTeacher">
      <!-- Cursos Disponibles -->
      <b-col md="6">
        <b-card title="Cursos Disponibles para Asignar">
          <div v-if="loadingCourses" class="text-center p-4">
            <b-spinner small></b-spinner>
            <span class="ml-2">Cargando cursos...</span>
          </div>
          <div v-else class="drag-drop-wrapper">
            <draggable
              v-model="availableCourses"
              class="drag-drop-container available-courses"
              group="courses"
              :sort="false"
              :animation="200"
              :empty-insert-threshold="10"
              @start="onDragStart"
              @end="onDragEnd"
              @add="onCourseAdded"
              @remove="onCourseRemoved"
            >
              <b-list-group-item
                v-for="course in availableCourses"
                :key="course.cursoId"
                class="drag-item"
              >
                <div class="course-content">
                  <div>
                    <strong>{{ course.codigo }}</strong> - {{ course.nombre }}
                    <br>
                    <small class="text-muted">{{ course.creditos }} créditos</small>
                  </div>
                  <div class="drag-handle">
                    <feather-icon icon="MoveIcon" class="text-muted" />
                  </div>
                </div>
              </b-list-group-item>
              
              <!-- Elemento placeholder invisible que siempre está presente -->
              <div v-if="availableCourses.length === 0" class="invisible-placeholder"></div>
            </draggable>
            
            <!-- Mensaje visual separado para estado vacío -->
            <div v-if="availableCourses.length === 0 && !loadingCourses" class="empty-state-overlay">
              <div class="text-center p-5 text-muted">
                <feather-icon icon="InboxIcon" size="32" class="mb-2" />
                <p class="mb-1">No hay cursos disponibles</p>
                <small>Todos los cursos han sido asignados</small>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>

      <!-- Cursos Autorizados -->
      <b-col md="6">
        <b-card title="Cursos Autorizados">
          <div v-if="loadingCompetencies" class="text-center p-4">
            <b-spinner small></b-spinner>
            <span class="ml-2">Cargando competencias...</span>
          </div>
          <div v-else class="drag-drop-wrapper">
            <draggable
              v-model="authorizedCourses"
              class="drag-drop-container authorized-courses"
              group="courses"
              :sort="true"
              :animation="200"
              :empty-insert-threshold="10"
              @start="onDragStart"
              @end="onDragEnd"
              @add="onCourseAdded"
              @remove="onCourseRemoved"
            >
              <b-list-group-item
                v-for="course in authorizedCourses"
                :key="course.cursoId"
                class="drag-item"
              >
                <div class="course-content">
                  <div>
                    <strong>{{ course.codigo }}</strong> - {{ course.nombre }}
                    <br>
                    <small class="text-muted">{{ course.creditos }} créditos</small>
                  </div>
                  <div class="d-flex align-items-center">
                    <div class="drag-handle mr-2">
                      <feather-icon icon="MoveIcon" class="text-muted" />
                    </div>
                    <b-button variant="flat-danger" class="btn-icon" size="sm" @click.stop="unauthorizeCourse(course)">
                      <feather-icon icon="XIcon" />
                    </b-button>
                  </div>
                </div>
              </b-list-group-item>
              
              <!-- Elemento placeholder invisible que siempre está presente -->
              <div v-if="authorizedCourses.length === 0" class="invisible-placeholder"></div>
            </draggable>
            
            <!-- Mensaje visual separado para estado vacío -->
            <div v-if="authorizedCourses.length === 0 && !loadingCompetencies" class="empty-state-overlay">
              <div class="text-center p-5 text-muted">
                <feather-icon icon="InboxIcon" size="32" class="mb-2" />
                <p class="mb-1">No hay cursos autorizados</p>
                <small>Arrastre cursos desde el panel izquierdo para autorizarlos</small>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Toast Container -->
    <div class="toast-container"></div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BListGroupItem, BButton, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import draggable from 'vuedraggable'
import { competenciasService } from '@/services/competenciasService'
import catedraticosService from '@/services/catedraticosService'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BListGroupItem,
    BButton,
    BSpinner,
    vSelect,
    draggable,
  },
  data() {
    return {
      teachers: [],
      selectedTeacher: null,
      authorizedCourses: [],
      availableCourses: [],
      loadingTeachers: false,
      loadingCompetencies: false,
      loadingCourses: false,
      saving: false,
      isDragging: false,
    }
  },
  async mounted() {
    await this.loadTeachers()
  },
  methods: {
    async loadTeachers() {
      this.loadingTeachers = true
      try {
        const response = await catedraticosService.getAll()
        console.log('Docentes cargados:', response)
        
        this.teachers = response.map(teacher => ({
          catedraticoId: teacher.catedraticoId,
          nombre: teacher.nombreCompleto || 'Sin nombre',
          especialidad: teacher.especialidad
        }))
        
        console.log('Docentes procesados:', this.teachers)
      } catch (error) {
        console.error('Error al cargar docentes:', error)
        this.showToast('Error al cargar los docentes', 'danger')
      } finally {
        this.loadingTeachers = false
      }
    },
    async loadCompetencies() {
      if (!this.selectedTeacher) {
        this.authorizedCourses = []
        this.availableCourses = []
        return
      }

      this.loadingCompetencies = true
      this.loadingCourses = true

      try {
        // Cargar competencias existentes del docente
        const competenciesData = await competenciasService.getByCatedratico(this.selectedTeacher.catedraticoId)
        console.log('Competencias del docente:', competenciesData)
        
        // Cargar cursos disponibles
        const cursosDisponibles = await competenciasService.getCursosDisponibles(this.selectedTeacher.catedraticoId)
        console.log('Cursos disponibles:', cursosDisponibles)

        // Configurar cursos autorizados y disponibles
        this.authorizedCourses = competenciesData.catedraticoCursos?.map(comp => ({
          cursoId: comp.cursoId,
          codigo: comp.cursoCodigo,
          nombre: comp.cursoNombre,
          creditos: comp.cursoCreditos,
          competenciaId: comp.catedraticoCursoId
        })) || []

        this.availableCourses = cursosDisponibles?.map(curso => ({
          cursoId: curso.cursoId,
          codigo: curso.codigo,
          nombre: curso.nombre,
          creditos: curso.creditos
        })) || []

        console.log('Cursos autorizados:', this.authorizedCourses)
        console.log('Cursos disponibles:', this.availableCourses)
      } catch (error) {
        console.error('Error al cargar competencias:', error)
        this.showToast('Error al cargar las competencias del docente', 'danger')
      } finally {
        this.loadingCompetencies = false
        this.loadingCourses = false
      }
    },
    async saveCompetencies() {

      if (!this.selectedTeacher) return;

      this.saving = true;
      try {
        console.log('Guardando competencias para docente:', this.selectedTeacher.catedraticoId);

        // Obtener competencias actuales desde el backend (estado original)
        const originalCompetencies = await competenciasService.getByCatedratico(this.selectedTeacher.catedraticoId);
        const originalCompetenciaIds = originalCompetencies.catedraticoCursos?.map(c => c.catedraticoCursoId) || [];
        const originalCourseIds = originalCompetencies.catedraticoCursos?.map(c => c.cursoId) || [];

        // Obtener IDs de cursos actualmente autorizados en la interfaz (estado nuevo)
        const currentCourseIds = this.authorizedCourses.map(c => c.cursoId);

        console.log('Cursos originales en BD:', originalCourseIds);
        console.log('Cursos actuales en UI:', currentCourseIds);

        // 1️⃣ Eliminar competencias que estaban antes pero ya no están autorizadas
        const competenciasAEliminar = originalCompetencies.catedraticoCursos?.filter(
          c => !currentCourseIds.includes(c.cursoId)
        ) || [];

        for (const competencia of competenciasAEliminar) {
          console.log('Eliminando competencia:', competencia.catedraticoCursoId, competencia.cursoNombre);
          await competenciasService.delete(competencia.catedraticoCursoId);
        }

        // 2️⃣ Agregar nuevas competencias (cursos que no existían antes)
        const cursosANuevos = this.authorizedCourses.filter(
          c => !originalCourseIds.includes(c.cursoId)
        );

        for (const course of cursosANuevos) {
          console.log('Creando nueva competencia para curso:', course.cursoId);
          await competenciasService.create({
            catedraticoId: this.selectedTeacher.catedraticoId,
            cursoId: course.cursoId,
            estado: true
          });
        }

        // ✅ Mostrar mensaje y recargar datos
        this.showToast('Competencias actualizadas correctamente', 'success');
        await this.loadCompetencies();

      } catch (error) {
        console.error('Error al guardar competencias:', error);
        this.showToast('Error al guardar las competencias', 'danger');
      } finally {
        this.saving = false;
      }
    },
    unauthorizeCourse(course) {
      const index = this.authorizedCourses.findIndex(c => c.cursoId === course.cursoId)
      if (index > -1) {
        const [removedCourse] = this.authorizedCourses.splice(index, 1)
        
        // Verificar si el curso ya existe en disponibles
        const existsInAvailable = this.availableCourses.some(c => c.cursoId === removedCourse.cursoId)
        if (!existsInAvailable) {
          this.availableCourses.push({
            cursoId: removedCourse.cursoId,
            codigo: removedCourse.codigo,
            nombre: removedCourse.nombre,
            creditos: removedCourse.creditos
          })
        }
        
        this.logCurrentState()
      }
    },
    onDragStart(event) {
      console.log('Iniciando drag:', event)
      this.isDragging = true
      event.item.classList.add('dragging')
    },
    onDragEnd(event) {
      console.log('Terminando drag:', event)
      this.isDragging = false
      event.item.classList.remove('dragging')
      this.logCurrentState()
    },
    onCourseAdded(event) {
      console.log('Curso agregado via drag:', event)
      
      // Cuando se agrega un curso a autorizados, debemos asegurarnos de que se quite de disponibles
      if (event.to.classList.contains('authorized-courses')) {
        const addedElement = event.item
        const cursoId = this.getElementCursoId(addedElement)
        
        if (cursoId) {
          // Remover de disponibles si existe allí
          const availableIndex = this.availableCourses.findIndex(c => c.cursoId === cursoId)
          if (availableIndex > -1) {
            this.availableCourses.splice(availableIndex, 1)
          }
        }
      }
      
      this.logCurrentState()
    },
    onCourseRemoved(event) {
      console.log('Curso removido via drag:', event)
      
      // Cuando se remueve un curso de autorizados (se mueve a disponibles)
      if (event.from.classList.contains('authorized-courses')) {
        const removedElement = event.item
        const cursoId = this.getElementCursoId(removedElement)
        
        if (cursoId) {
          // Agregar a disponibles si no existe allí
          const existsInAvailable = this.availableCourses.some(c => c.cursoId === cursoId)
          if (!existsInAvailable) {
            const removedCourse = this.authorizedCourses.find(c => c.cursoId === cursoId)
            if (removedCourse) {
              this.availableCourses.push({
                cursoId: removedCourse.cursoId,
                codigo: removedCourse.codigo,
                nombre: removedCourse.nombre,
                creditos: removedCourse.creditos
              })
            }
          }
        }
      }
      
      this.logCurrentState()
    },
    getElementCursoId(element) {
      // Obtener el cursoId del elemento arrastrado
      const courseElement = element.querySelector('.drag-item')
      if (courseElement) {
        const keyAttr = courseElement.getAttribute('key')
        return keyAttr ? parseInt(keyAttr) : null
      }
      return null
    },
    logCurrentState() {
      console.log('=== ESTADO ACTUAL ===')
      console.log('Cursos disponibles:', this.availableCourses.length, this.availableCourses)
      console.log('Cursos autorizados:', this.authorizedCourses.length, this.authorizedCourses)
    },
    showToast(message, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Éxito' : variant === 'warning' ? 'Advertencia' : 'Error',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          variant: variant,
          text: message,
        },
      })
    }
  },
  watch: {
    selectedTeacher() {
      this.authorizedCourses = []
      this.availableCourses = []
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

/* Contenedores principales de drag & drop */
.drag-drop-container {
  min-height: 250px;
  height: 100%;
  padding: 1rem;
  border-radius: 0.375rem;
  transition: all 0.3s ease;
  
  /* Estilo cuando está vacío y listo para recibir elementos */
  &:has(.invisible-placeholder:only-child) {
    border: 2px dashed #dee2e6;
    background-color: #fafafa;
  }
  
  /* Estilo durante el drag sobre el área */
  &.sortable-ghost,
  &.sortable-chosen {
    border: 2px dashed #28a745 !important;
    background-color: rgba(40, 167, 69, 0.05) !important;
  }
}

/* Wrapper para ambas áreas de drag & drop */
.drag-drop-wrapper {
  position: relative;
  min-height: 250px;
  height: 100%;
}

/* Items draggables */
.drag-item {
  cursor: move;
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;
  border-radius: 0.375rem !important;
  border: 1px solid #e9ecef;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:hover {
    background-color: #f8f9fa;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    border-color: #007bff;
  }
  
  &.dragging {
    opacity: 0.7;
    transform: rotate(2deg);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }
}

/* Contenido de los cursos */
.course-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

/* Handle para drag */
.drag-handle {
  cursor: grab;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  &:active {
    cursor: grabbing;
  }
}

/* Placeholder invisible */
.invisible-placeholder {
  visibility: hidden;
  height: 0;
  min-height: 0;
  padding: 0;
  margin: 0;
  border: none;
}

/* Overlay para estado vacío */
.empty-state-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 2;
}

/* Estados de drag & drop */
.sortable-ghost {
  opacity: 0.5;
  background-color: #e3f2fd !important;
  border: 2px dashed #2196f3 !important;
}

.sortable-chosen {
  cursor: move;
}

.sortable-drag {
  opacity: 0.8;
  transform: rotate(5deg);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

/* Asegurar que los botones no interfieran con el drag */
.btn-icon {
  z-index: 1;
}

/* Efectos visuales durante el drag */
.draggable-mirror {
  z-index: 9999 !important;
  opacity: 0.9;
  transform: rotate(3deg);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  border: 1px solid #007bff !important;
}
</style>